import { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import useLoggedInUser from '@rsos/hooks/useLoggedInUser';
import useHandleUnreadConversations from '@rsos/reduxDataStore/src/features/conversations/useHandleUnreadConversations';
import useHandleUnreadCollaborator from '@rsos/reduxDataStore/src/features/emergencyCalls/useHandleUnreadCollaborator';
import { filteredAlertsSelector } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import { handleUnreadAlerts } from '@rsos/utils/handleReadUnreadAlerts';
import { captureException } from '@rsos/utils/sentry';
import { createTalkSession } from '@rsos/utils/talkJS';
import { handleUnreadCalls } from '../utils/handleReadUnreadCalls';
import { handleTF911UnreadMessages } from '../utils/handleTF911UnreadMessages';

/**
 * Track unread alerts and calls via TalkJS when no alert or call is selected,
 * or when a call is selected but not shared or received via agency share/chat.
 */
const useTrackUnreads = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({ psaps: state.psaps, sinatra: state.sinatra }),
  );
  const rawCallQueue = useSelector(state => state.emergencyCalls.callQueue);
  const filteredCalls = useSelector(state =>
    callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess),
  );

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const isCallReceived =
    filteredCalls?.[selectedCallerID]?.agencyShareChat?.isCallReceived;

  const isCallShared =
    filteredCalls?.[selectedCallerID]?.agencyShareChat?.isCallShared;

  const alertsQueue = useSelector(state =>
    filteredAlertsSelector(state.incidents),
  );

  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);

  const latestMsgTime = useSelector(state => state.incidents.latestMsgTime);

  const loggedInUser = useLoggedInUser('rsp_calls_user');

  const [chatSession, setChatSession] = useState(null);

  useHandleUnreadConversations(chatSession);
  useHandleUnreadCollaborator(chatSession);

  useEffect(() => {
    let session;

    try {
      (async () => {
        // No role is set here as the purpose of this hook is to just track
        // unread messages for both calls and alerts.
        session = await createTalkSession(loggedInUser);

        setChatSession(session);
      })();
    } catch (error) {
      captureException(error);
    }
  }, [loggedInUser]);

  useEffect(() => {
    // All events view
    const noCallOrAlertSelected = !selectedAlertID && !selectedCallerID;

    // When a call is selected but not shared or received
    const selectedCallNotReceivedOrShared =
      selectedCallerID && !isCallReceived && !isCallShared;

    if (
      chatSession &&
      (noCallOrAlertSelected || selectedCallNotReceivedOrShared)
    ) {
      if (Object.keys(filteredCalls).length) {
        handleUnreadCalls(
          chatSession,
          filteredCalls,
          selectedCallerID,
          dispatch,
        );
      }

      if (Object.keys(alertsQueue).length) {
        handleUnreadAlerts(
          chatSession,
          alertsQueue,
          selectedAlertID,
          latestMsgTime,
          dispatch,
        );
      }
    }

    // always set up TF911 global message listener (use raw call queue to avoid filtering issues)
    if (chatSession && Object.keys(rawCallQueue).length) {
      handleTF911UnreadMessages(
        chatSession,
        dispatch,
        store.getState, // pass getState function to access textFrom911 data
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chatSession,
    isCallReceived,
    isCallShared,
    selectedAlertID,
    selectedCallerID,
    rawCallQueue,
  ]);
};

export default useTrackUnreads;
